import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";

/**
 * Shared drag-and-drop / click-to-browse file picker.
 *
 * Use this for ANY file upload surface instead of hand-rolling drag handlers.
 * It owns only the picking concern — drag state, click/keyboard-to-browse,
 * extension and size checks — and hands the raw File objects up. Parsing,
 * uploading, and error presentation stay with the caller.
 *
 * Errors are reported through `onError(message)` rather than being rendered
 * here, so each screen can surface them its own way (errorToast, inline text).
 *
 * Anything passed as `children` renders inside the zone with its clicks
 * isolated, so nested buttons (e.g. "Download sample template") do not also
 * trigger the file browser.
 *
 * @example
 * <FileDropZone
 *   accept={[".csv", ".xlsx"]}
 *   maxSizeMb={10}
 *   busy={parsing}
 *   busyTitle="Reading your file…"
 *   disabled={!canUpload}
 *   onFilesSelected={(files) => handleFile(files[0])}
 *   onError={(message) => errorToast(message)}
 * >
 *   <button type="button" onClick={downloadTemplate}>Download sample template</button>
 * </FileDropZone>
 */
const FileDropZone = ({
  // What may be picked. Entries are extensions (".csv") or mime types
  // ("image/png"); an empty list accepts anything.
  accept = [],
  multiple = false,
  maxSizeMb,

  // State
  disabled = false,
  busy = false,

  // Copy. `description` defaults to listing the accepted types.
  title = "Drag and drop your file here",
  description,
  busyTitle = "Uploading…",
  busyDescription = "",

  icon,
  minHeight = "14rem",
  className = "",

  onFilesSelected = () => {},
  onError = () => {},

  children,
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const inert = disabled || busy;

  const acceptList = Array.isArray(accept) ? accept : [accept];
  const defaultDescription = acceptList.length
    ? `or click to browse — supports ${acceptList.join(", ")}`
    : "or click to browse";

  const isAccepted = (file) => {
    if (acceptList.length === 0) return true;
    const name = (file.name || "").toLowerCase();
    return acceptList.some((entry) => {
      const rule = String(entry).trim().toLowerCase();
      if (!rule) return false;
      // Mime rules ("image/png", "image/*") match on file.type.
      if (rule.includes("/")) {
        if (rule.endsWith("/*")) {
          return (file.type || "").startsWith(rule.slice(0, -1));
        }
        return (file.type || "").toLowerCase() === rule;
      }
      return name.endsWith(rule);
    });
  };

  const openFileBrowser = () => {
    if (!inert) inputRef.current?.click();
  };

  const handleFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;

    const files = multiple ? picked : picked.slice(0, 1);

    const wrongType = files.find((file) => !isAccepted(file));
    if (wrongType) {
      onError(
        `Unsupported file type. Upload a ${acceptList.join(" or ")} file.`
      );
      return;
    }

    if (maxSizeMb) {
      const tooBig = files.find((file) => file.size > maxSizeMb * 1024 * 1024);
      if (tooBig) {
        onError(`"${tooBig.name}" is larger than ${maxSizeMb} MB.`);
        return;
      }
    }

    onFilesSelected(files);
  };

  return (
    <div
      onClick={openFileBrowser}
      onDragOver={(event) => {
        event.preventDefault();
        if (!inert) setDragActive(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        if (inert) return;
        handleFiles(event.dataTransfer?.files);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openFileBrowser();
        }
      }}
      role="button"
      tabIndex={inert ? -1 : 0}
      aria-disabled={inert}
      aria-label={title}
      className={`rounded-[12px] border border-dashed flex flex-col items-center justify-center gap-2 py-10 px-4 transition-colors duration-150 focus_outline__none ${
        inert ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      } ${className}`}
      style={{
        minHeight,
        borderColor: dragActive ? "var(--primary)" : "var(--dashboard-gray-300)",
        backgroundColor: dragActive
          ? "var(--trans-stats-con-blue-bg)"
          : "var(--dashboard-gray-50)",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        className="hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          // Reset so re-picking the same filename still fires onChange.
          event.target.value = "";
        }}
      />

      {icon ?? <Upload size={28} color="var(--dashboard-gray-500)" />}

      <span className="text-sm font-[500] text-[var(--dashboard-text-main)] mt-1">
        {busy ? busyTitle : title}
      </span>
      <span className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-secondary)] text-center">
        {busy ? busyDescription : description ?? defaultDescription}
      </span>

      {children ? (
        // Isolated so nested controls don't also open the file browser.
        <div className="mt-3" onClick={(event) => event.stopPropagation()}>
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default FileDropZone;
